import { useLazyQuery } from '@apollo/client'
import { createContext, useContext, useReducer } from 'react'
import useToast from 'shared/components/Toast/useToast'
import { GetNotifications } from 'shared/graphql/notifications.gql'

import { GetNotificationsQuery } from '__generated__/graphql'
import type { Dispatch, ReactNode } from 'react'

interface NotificationContextProps {
    notifications: NotificationState
    dispatchNotifications: Dispatch<NotificationAction>
    checkNotifications: () => void
}

type NotificationState = GetNotificationsQuery['notifications']['data']

type NotificationAction =
    | { type: 'SET_NOTIFICATIONS'; payload: NotificationState }
    | { type: 'MARK_AS_READ'; payload: string }

const NotificationContext = createContext<NotificationContextProps | undefined>(
    undefined
)

const notificationReducer = (
    state: NotificationState,
    action: NotificationAction
): NotificationState => {
    switch (action.type) {
        case 'SET_NOTIFICATIONS':
            return action.payload
        case 'MARK_AS_READ':
            return state.map((notification) =>
                notification.id === action.payload
                    ? { ...notification, isRead: true }
                    : notification
            )
        default:
            throw new Error(`Unknown action`)
    }
}

interface NotificationProviderProps {
    children: ReactNode
}

export const NotificationProvider = ({
    children,
}: NotificationProviderProps) => {
    const [notifications, dispatchNotifications] = useReducer(
        notificationReducer,
        []
    )
    const toast = useToast()

    const [checkNotifications] = useLazyQuery(GetNotifications, {
        fetchPolicy: 'network-only',
        variables: { page: 1, limit: 10 },
        onCompleted(data) {
            dispatchNotifications({
                type: 'SET_NOTIFICATIONS',
                payload: data.notifications.data,
            })
        },
        onError(error) {
            console.log('Error with GetNotifications:', error)
            toast({
                title: 'Something went wrong',
                message: 'Please try again later',
            })
        },
    })

    return (
        <NotificationContext.Provider
            value={{ checkNotifications, notifications, dispatchNotifications }}
        >
            {children}
        </NotificationContext.Provider>
    )
}

export const useNotifications = (): NotificationContextProps => {
    const context = useContext(NotificationContext)
    if (!context) {
        throw new Error(
            'useNotifications must be used within a NotificationProvider'
        )
    }
    return context
}
